<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells position="between">
        <h1 class="title">Payment ID: {{ this.$route.params.id }}</h1>
        <app-cells :indent="false">
          <app-button v-if="!payment_data.date_received" theme="info" @click.native="$modal.show('date')">Received</app-button>
          <app-button v-if="payment_data.date_received && !payment_data.efd_receipt" ref="post_receipt" @click.native="onPostReceipt">EFD receipt</app-button>
          <app-button v-if="!payment_data.efd_receipt" theme="error" @click.native="$modal.show('delete')">Delete</app-button>
        </app-cells>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <button
            v-if="!payment_data.efd_receipt"
            class="info-cards__control"
            @click="onShowEditModal"
          >
            <img src="@/assets/img/edit-table-icon.svg" alt="">
          </button>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Date:</span> <span>{{ formatDate(payment_data.date) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Date received:</span> <span>{{ formatDate(payment_data.date_received) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Currency:</span> <span>{{ payment_data.currency.code }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Amount:</span> <span>{{ formatAmount(payment_data.amount) }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Amount TZS:</span> <span>{{ formatAmount(payment_data.amount_tzs) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Amount USD:</span> <span>{{ formatAmount(payment_data.amount_usd) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Allocated:</span> <span>{{ formatAmount(payment_data.allocated_amount) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Unallocated:</span> <span>{{ formatAmount(payment_data.remaining_amount) }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card" v-if="payment_data.efd_receipt">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>EFD receipt ID:</span> <span>{{ payment_data.efd_receipt.id }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Verification code:</span> <span>{{ payment_data.efd_receipt.verification_code }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Amount:</span> <span>{{ formatAmount(payment_data.efd_receipt.amount) }}</span>
            </div>
          </div>
        </div>
      </div>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
      >
        <template #h__group><div class="table-default__left">Group</div></template>
        <template #group="{ row }">
          <div class="table-default__left">{{ row.group.name }} ({{ row.group.id }})</div>
        </template>
        <template #permits_amount="{ row }">{{ formatAmount(row.permits_amount) }}</template>
        <template #crew_salary_amount="{ row }">{{ formatAmount(row.crew_salary_amount) }}</template>
        <template #clients_revenue_amount="{ row }">{{ formatAmount(row.clients_revenue_amount) }}</template>
        <template #total_amount="{ row }">{{ formatAmount(row.total_amount) }}</template>
      </v-client-table>
    </template>
    <modal
      name="edit_payment"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('edit_payment')"></button>
        <div class="popup__title">Edit payment</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Date" required>
              <app-date-picker
                v-model="form.date"
                placeholder="Date"
                :error="$v.form.date.$error"
              />
              <template #error>
                <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Amount" label-for="amount" required>
              <app-fin-number
                v-model="form.amount"
                id="amount"
                name="amount"
                :error="$v.form.amount.$error"
              />
              <template #error>
                <div v-if="$v.form.amount.$dirty && !$v.form.amount.required">Required field</div>
              </template>
            </app-form-group>

            <app-cells :indent="false" position="center">
              <app-button ref="submit" :disabled="$v.form.$error" type="submit">Save</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete payment
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this payment?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeletePayment" ref="deletePayment">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="date"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('date')" class="popup__close" type="button"></button>
        <div class="popup__title">Set received date</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFormDate">
            <app-form-group label="Date" required>
              <app-date-picker
                v-model="date_received"
                placeholder="Date"
                :error="$v.date_received.$error"
              />
              <template #error>
                <div v-if="$v.date_received.$dirty && !$v.date_received.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit_date">Save</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteClimbingReimbursement,
  getClimbingReimbursement,
  patchClimbingGroupsReimbursementReceived,
  patchClimbingReimbursement,
  postFinanceReimbursementEFD,
} from '@/http'
import { format, parseISO } from 'date-fns'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'AlturaReimbursementDetail',
  data() {
    return {
      page_loaded: false,
      payment_data: {},
      form: {},
      date_received: null,
      table: {
        items: [],
        columns: [
          'group',
          'permits_amount',
          'crew_salary_amount',
          'clients_revenue_amount',
          'total_amount',
        ],
        options: {
          headings: {
            'group': 'Group',
            'permits_amount': 'Permits',
            'crew_salary_amount': 'Crew salary',
            'clients_revenue_amount': 'Clients revenue',
            'total_amount': 'Total amount',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
    }
  },
  validations: {
    form: {
      date: { required },
      amount: { required },
    },
    date_received: { required },
  },
  created() {
    this.fetchPaymentData()
  },
  methods: {
    fetchPaymentData() {
      getClimbingReimbursement(Number(this.$route.params.id))
        .then(response => {
          this.payment_data = response.data
          this.table.items = response.data.allocations
          this.page_loaded = true
        })
    },
    onShowEditModal() {
      this.form = {
        amount: this.payment_data.amount,
        date: this.payment_data.date,
      }
      this.$v.form.$reset()
      this.$modal.show('edit_payment')
    },
    onPostReceipt() {
      this.$refs.post_receipt.preload = true
      postFinanceReimbursementEFD(Number(this.$route.params.id))
        .then(() => {
          this.fetchPaymentData()
          this.$notify({
            type: 'success',
            title: 'Receipt issued'
          })
          this.$refs.post_receipt.preload = false
        })
        .catch(() => { this.$refs.post_receipt.preload = false })
    },
    onCheckFormDate() {
      this.$v.date_received.$touch()
      if (this.$v.date_received.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly'
        })
      } else {
        this.sendFormDate()
      }
    },
    sendFormDate() {
      this.$refs.submit_date.preload = true
      patchClimbingGroupsReimbursementReceived(Number(this.$route.params.id), { date_received: this.date_received })
        .then(() => {
          this.fetchPaymentData()
          this.$notify({
            type: 'success',
            title: 'Changes saved'
          })
          this.$modal.hide('date')
        })
        .catch(() => { this.$refs.submit_date.preload = false })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly'
        })
      } else {
        this.sendForm()
      }
    },
    async sendForm() {
      this.$refs.submit.preload = true
      patchClimbingReimbursement(Number(this.$route.params.id), this.form)
        .then(() => {
          this.fetchPaymentData()
          this.$notify({
            type: 'success',
            title: 'Changes saved'
          })
          this.$modal.hide('edit_payment')
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onDeletePayment() {
      this.$refs.deletePayment.preload = true
      deleteClimbingReimbursement(Number(this.$route.params.id))
        .then(() => {
          this.$router.push({name: 'finance-altura-reimbursement-payments'})
        })
        .catch(() => {
          this.$refs.deletePayment.preload = false
        })
    },
    formatDate(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    formatAmount(string) {
      return string ? new Intl.NumberFormat('ru-RU').format(string) : '-'
    },
  }
}
</script>
